import type React from "react";
import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { externalUrlHref, openExternalUrl, parseLinkedInUrl } from "@/utils/external-url";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { CandidateHistoryModal } from "./CandidateHistoryModal";
import { AtualizarDisponibilidadeDialog } from "@/components/freelancers/AtualizarDisponibilidadeDialog";
import { ObservacoesModal } from "./ObservacoesModal";
import { DropCandidateModal } from "./DropCandidateModal";
import { DropInPlaceModal } from "./DropInPlaceModal";
import { DropInPlaceListModal } from "./DropInPlaceListModal";
import { FreelancerProjetosModal } from "./FreelancerProjetosModal";
import { NegociacaoModal } from "./NegociacaoModal";
import { FalarComFrilaModal } from "@/components/atividades/FalarComFrilaModal";
import type { PendingActivityInfo } from "@/types/activity";
import { formatMotivosLista, hasMotivos, parseDropReasons } from "@/utils/motivosUtils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useKanbanApi } from "@/utils/kanban-api";
import { useAuth } from "@/contexts/AuthContext";
import { useFreelancersInvalidate } from "@/hooks/use-freelancers-invalidate";
import { useToast } from "@/hooks/use-toast";
import { createLocalDate, calcularDiasRestantes } from "@/utils/dateUtils";
import { FreelancerStatus } from "@/types/freelancer-rating";
import { KanbanCard } from "@/types/kanban";
import { supabase } from "@/integrations/supabase/client";
import { PlusCircle, RefreshCw, GripVertical, Clock, ArrowRight, DollarSign, Briefcase, MoreVertical, ArrowUpDown, ChevronRight, Calendar, FileText, MessageSquare, AlertCircle, Globe, EyeOff, FolderOpen, FolderClosed, Building2, MapPin, Copy, CheckSquare, Handshake, HeartHandshake, Radar, Pencil } from "lucide-react";
import { Linkedin } from "@/components/icons/linkedin";
import { Checkbox } from "@/components/ui/checkbox";
import { EnviarParaVagaDialog } from "@/components/vagas/EnviarParaVagaDialog";
import { DisponibilidadeLinha } from "@/components/freelancers/DisponibilidadeBadge";
import { useDisponibilidadeBatch } from "@/hooks/useDisponibilidade";
import { useCopyFreelancerDetails } from "@/hooks/use-copy-freelancer-details";
import { CopyFallbackDialog } from "@/components/freelancers/CopyFallbackDialog";
import { ClipboardCopy, ClipboardList } from "lucide-react";
import { trackFreelancerView } from "@/hooks/use-track-freelancer-view";
import { FreelancerMultiSelectBar } from "@/components/freelancers/v2/FreelancerMultiSelectBar";
import { FreelancerCompareDrawer } from "@/components/freelancers/v2/FreelancerCompareDrawer";
import { FreelancerScoreBadge } from "@/components/freelancers/v2/FreelancerScoreBadge";
import { IndicarAgenciaDialog } from "@/components/freelancers/IndicarAgenciaDialog";
import { useIndicacoesAgencia } from "@/hooks/use-indicacoes-agencia";
import type { FreelancerListItem } from "@/types/freelancer";
import type { Vaga } from "@/types/vaga";
import {
  formatStatusEtapaText,
  countCardsComNegociacao,
  negociacaoStatusLabel,
  negociacaoStatusIcon,
} from "@/utils/kanban-status-text";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ExternalLinkButton } from "@/components/ui/external-link-button";
import { FreelancerLinkedInButton } from "@/components/freelancers/painel/FreelancerLinkedInButton";
import { useSidePanel } from "@/components/patterns";
import { CandidatoPanel, type CandidatoPanelTab } from "@/components/freelancers/painel/CandidatoPanel";
import { useRegistrarPainelDaPagina } from "@/components/freelancers/painel/painel-do-candidato";
import { projectArea } from "@/utils/project-area";
import { reportUserError } from "@/lib/report-error";

interface VagaFreelancers2TabProps {
  vagaId: string;
  initialEtapaId?: string;
}

// Função para gerar iniciais do nome
const getInitials = (name: string): string => {
  const words = name.trim().split(' ').filter(word => word.length > 0);
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

// Função para formatar pretensão salarial
const formatCurrency = (value?: number | string): string => {
  if (!value) return "";
  if (typeof value === 'string') return value;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};


export function VagaFreelancers2Tab({ vagaId, initialEtapaId }: VagaFreelancers2TabProps) {
  const navigate = useNavigate();
  const candidatoPanel = useSidePanel<CandidatoPanelTab>("candidato");
  useRegistrarPainelDaPagina(candidatoPanel.open);
  const openCandidatoOnPlainClick = (event: React.MouseEvent, freelancerId: string) => {
    event.stopPropagation();
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    trackFreelancerView(freelancerId, "painel_candidato", { vaga_id: vagaId, origem_tela: "kanban" });
    candidatoPanel.open(freelancerId);
  };
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const kanbanApi = useKanbanApi();
  const invalidateFreelancers = useFreelancersInvalidate();
  const {
    copyDetails: copyFreelancerDetails,
    copyMultipleDetails: copyFreelancerDetailsList,
    fallbackOpen: copyFallbackOpen,
    setFallbackOpen: setCopyFallbackOpen,
    fallbackText: copyFallbackText,
  } = useCopyFreelancerDetails();

  const [historyCandidate, setHistoryCandidate] = useState<KanbanCard | null>(null);
  const [draggedOverId, setDraggedOverId] = useState<string | null>(null);
  const [disponibilidadeCard, setDisponibilidadeCard] = useState<KanbanCard | null>(null);
  const [observacoesCard, setObservacoesCard] = useState<KanbanCard | null>(null);
  const [dropCandidate, setDropCandidate] = useState<KanbanCard | null>(null);
  const [dropInPlaceCandidate, setDropInPlaceCandidate] = useState<KanbanCard | null>(null);
  const [showDropInPlaceList, setShowDropInPlaceList] = useState(false);
  const [selectedFreelancerProjetos, setSelectedFreelancerProjetos] = useState<{freelancerId: string, nome: string} | null>(null);
  // Radar de perfis: candidato selecionado para abrir o diálogo de indicação por agência.
  const [indicarCandidate, setIndicarCandidate] = useState<{freelancerId: string, nome: string} | null>(null);
  const [selectedEtapaId, setSelectedEtapaId] = useState<string>("");
  const hasAppliedInitialEtapa = useRef(false);

  // Estado para copiar freelancers para outra vaga
  // Seleção múltipla sem "modo": o checkbox aparece no hover do card e a seleção
  // ativa (size > 0) mantém todos visíveis + barra flutuante com as ações em massa.
  const [selectedCardIds, setSelectedCardIds] = useState<Set<string>>(new Set());
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyFreelancers, setCopyFreelancers] = useState<{ id: string; name: string }[]>([]);
  const [compareDrawerOpen, setCompareDrawerOpen] = useState(false);
  const [freelancersToCompare, setFreelancersToCompare] = useState<FreelancerListItem[]>([]);
  // Candidato com modal de negociação aberto (ação disponível na etapa Shortlist).
  const [negociacaoCandidate, setNegociacaoCandidate] = useState<KanbanCard | null>(null);

  // Candidato com modal "Fazer Amizade" (falar_com_frila) aberto.
  const [falarComFrilaCard, setFalarComFrilaCard] = useState<KanbanCard | null>(null);
  const [falarComFrilaPending, setFalarComFrilaPending] = useState<PendingActivityInfo | null>(null);
  const [checkingFrilaId, setCheckingFrilaId] = useState<string | null>(null);

  // Abre o modal "Fazer Amizade", verificando antes se já existe atividade pendente
  // para o freelancer (para editar em vez de duplicar).
  const handleFalarComFrila = async (card: KanbanCard) => {
    if (checkingFrilaId) return;
    setCheckingFrilaId(card.id);
    try {
      const { data, error } = await supabase.rpc("get_pending_activity_for_freelancer", {
        p_freelancer_id: card.freelancer.id,
        p_tipo: "falar_com_frila",
      });
      if (error) throw error;
      setFalarComFrilaPending(data && data.length > 0 ? (data[0] as PendingActivityInfo) : null);
      setFalarComFrilaCard(card);
    } catch (error) {
      console.error("Erro ao verificar atividade:", error);
      toast({
        title: "Erro",
        description: "Falha ao verificar atividade existente.",
        variant: "destructive",
      });
    } finally {
      setCheckingFrilaId(null);
    }
  };

  // Ordenação dos cards dentro de cada etapa. "kanban" mantém ordem manual do
  // drag-and-drop; "score" reordena pelo score_reputacao desc.
  type SortMode = "kanban" | "score";
  const [sortMode, setSortMode] = useState<SortMode>("kanban");

  // Buscar dados da vaga (usado nas chips de match do drawer de comparação)
  const { data: vaga } = useQuery({
    queryKey: ["vaga", vagaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vagas")
        .select("*")
        .eq("id", vagaId)
        .single();
      if (error) throw error;
      return data as Vaga;
    },
  });

  // Radar de perfis: freelancers que dão match com a agência DESTA vaga.
  const { data: todasIndicacoes = [] } = useIndicacoesAgencia();
  const matchDaAgencia = useMemo(() => {
    const set = new Set<string>();
    if (!vaga?.agencia_id) return set;
    todasIndicacoes
      .filter((i) => i.agencia_id === vaga.agencia_id)
      .forEach((i) => set.add(i.freelancer_id));
    return set;
  }, [todasIndicacoes, vaga?.agencia_id]);

  // Buscar etapas
  const { data: etapas = [], isLoading: isLoadingEtapas } = useQuery({
    queryKey: ["kanban-etapas", vagaId],
    queryFn: () => kanbanApi.getEtapas(vagaId),
  });

  // Buscar cards (sem drop in-place)
  // PERF: uma única leitura de view_kanban_completo (todos os cards) com split no
  // client, em vez de 2 queries (getCards + getCardsDropInPlace) sobre a mesma view.
  const { data: allCards = [], isLoading: isLoadingCards } = useQuery({
    queryKey: ["kanban-cards", vagaId],
    queryFn: () => kanbanApi.getCards(vagaId, true),
  });
  const isInPlaceDrop = (c: { is_dropped?: boolean; drop_tipo?: string }) =>
    c.is_dropped === true && c.drop_tipo === "in_place";
  const cardsRaw = useMemo(() => allCards.filter((c) => !isInPlaceDrop(c)), [allCards]);
  const cardsDropInPlace = useMemo(() => allCards.filter(isInPlaceDrop), [allCards]);
  const isLoadingDropInPlace = isLoadingCards;

  // Negociação atual por posição (M101) — usada para gerar o texto "Copiar status".
  // Falha silenciosa se a view ainda não existir em ambientes não-migrados.
  const { data: negociacoesAtuais = {} } = useQuery({
    queryKey: ["kanban-negociacoes-atuais", vagaId],
    queryFn: () => kanbanApi.getNegociacoesAtuaisByVaga(vagaId),
  });

  // Enriquece cada card com sua negociação atual.
  const cardsData = useMemo(
    () =>
      cardsRaw.map((card) => ({
        ...card,
        negociacao_atual: negociacoesAtuais[card.id] ?? null,
      })),
    [cardsRaw, negociacoesAtuais],
  );


  // Buscar projetos em andamento dos freelancers no kanban
  const freelancerIds = useMemo(() => {
    return [...new Set(cardsData.map(card => card.freelancer_id))];
  }, [cardsData]);

  // Disponibilidade dos candidatos do kanban (golden record), hidratada em lote.
  const { data: dispKanbanMap } = useDisponibilidadeBatch(freelancerIds);

  // PERF: busca só os freelancers DESTA vaga (ids do kanban) + reputação, em vez
  // de carregar a tabela inteira (~5,5k, 6 páginas) via cache. Costuma ser dezenas.
  const { data: vagaFreelancers = [] } = useQuery({
    queryKey: ["vaga-freelancers-reput", freelancerIds.join(",")],
    enabled: freelancerIds.length > 0,
    staleTime: 60_000,
    queryFn: async (): Promise<FreelancerListItem[]> => {
      const [base, rep] = await Promise.all([
        supabase.from("freelancers").select("*").in("id", freelancerIds),
        supabase
          // v_freelancer_reputacao não está no types.ts gerado
          .from("v_freelancer_reputacao" as never)
          .select(
            "freelancer_id, score_reputacao, total_sinais, total_selecoes, total_shortlists, total_aprovacoes, total_projetos_concluidos, total_drops, taxa_conversao, total_vagas_selecionado, total_vagas_shortlist_inclusivo, taxa_conversao_shortlist, ultimo_sinal_at, total_views"
          )
          .in("freelancer_id", freelancerIds),
      ]);
      if (base.error) throw base.error;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const repMap = new Map((rep.data ?? []).map((r: any) => [r.freelancer_id, r]));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (base.data ?? []).map((f: any) => ({ ...f, ...(repMap.get(f.id) ?? {}) })) as FreelancerListItem[];
    },
  });

  const { data: projetosEmAndamento = new Map() } = useQuery({
    queryKey: ["projetos-andamento", freelancerIds.join(",")],
    queryFn: async () => {
      if (freelancerIds.length === 0) return new Map();
      
      const hoje = new Date().toISOString().split('T')[0]; // Data de hoje no formato YYYY-MM-DD
      
      const { data, error } = await supabase
        .from("controle_projetos")
        .select("id, freelancer_id, area_atuacao, area_canonica, data_inicio, data_conclusao, agencia:agencias(nome)")
        .in("freelancer_id", freelancerIds)
        .is("deleted_at", null)
        .or(`data_conclusao.is.null,data_conclusao.gte.${hoje}`) // Sem data de conclusão OU data de conclusão >= hoje
        .order("data_inicio", { ascending: false });
      
      if (error) {
        console.error("Erro ao buscar projetos em andamento:", error);
        throw error;
      }
      
      // Criar um Map com freelancer_id como chave e array de projetos como valor
      const projetosMap = new Map();
      (data || []).forEach(projeto => {
        if (!projetosMap.has(projeto.freelancer_id)) {
          projetosMap.set(projeto.freelancer_id, []);
        }
        projetosMap.get(projeto.freelancer_id).push(projeto);
      });
      
      return projetosMap;
    },
    enabled: freelancerIds.length > 0,
    staleTime: 60000, // Cache por 1 minuto
  });

  // Buscar métricas
  const { data: metricas, isLoading: isLoadingMetricas } = useQuery({
    queryKey: ["kanban-metricas", vagaId],
    queryFn: () => kanbanApi.getMetricas(vagaId),
  });

  // Contagem de observações de TODOS os cards numa única query (em vez de 1 por
  // card — era N+1 em view_kanban_posicoes_observacoes_completo). O badge do card
  // só precisa do número; o conteúdo completo é carregado pelo ObservacoesModal.
  const observacoesPosicaoIds = useMemo(
    () => [...new Set(cardsData.map((c) => c.id))].sort(),
    [cardsData],
  );
  const { data: observacoesCounts = {} } = useQuery({
    queryKey: ["kanban-observacoes-counts", vagaId, observacoesPosicaoIds.join(",")],
    queryFn: () => kanbanApi.getObservacoesCountsBatch(observacoesPosicaoIds),
    enabled: observacoesPosicaoIds.length > 0,
    staleTime: 30000,
  });

  // Buscar histórico quando um candidato é selecionado
  const { data: historico = [] } = useQuery({
    queryKey: ["kanban-historico", historyCandidate?.id],
    queryFn: () => historyCandidate ? kanbanApi.getHistorico(historyCandidate.id) : [],
    enabled: !!historyCandidate,
  });

  // Identificar etapa de drop (Reprovado)
  const reprovadoEtapa = useMemo(() => etapas.find(e => e.tipo === "drop"), [etapas]);
  
  // Buscar cards reprovados
  const cardsReprovados = useMemo(() => {
    if (!reprovadoEtapa) return [];
    return cardsData
      .filter((card) => card.etapa_atual_id === reprovadoEtapa.id)
      .sort((a, b) => a.ordem_na_etapa - b.ordem_na_etapa);
  }, [cardsData, reprovadoEtapa]);

  // Buscar histórico de drop para todos os cards reprovados.
  // 1 query em lote (getHistoricoBatch) em vez de 1 por card (era N+1).
  const { data: historicosDrop = [] } = useQuery({
    queryKey: ["kanban-historico-drop", [...cardsReprovados.map(c => c.id)].sort().join(",")],
    queryFn: async () => {
      if (cardsReprovados.length === 0) return [];
      const histPorCard = await kanbanApi.getHistoricoBatch(
        cardsReprovados.map((card) => card.id),
      );
      return cardsReprovados.map((card) => {
        const dropInfo = (histPorCard[card.id] || []).find(
          (h) => h.tipo_movimento === "drop",
        );
        return {
          cardId: card.id,
          motivo_drop: dropInfo?.motivo_drop,
          observacoes: dropInfo?.observacoes,
          usuario: dropInfo?.usuario,
        };
      });
    },
    enabled: cardsReprovados.length > 0,
    staleTime: 60000, // Cache por 1 minuto
  });

  // Mapa de informações de drop por card
  const dropInfoMap = useMemo(() => {
    const map = new Map<string, { 
      motivo_drop?: string[]; 
      observacoes?: string;
      usuario?: {
        full_name?: string;
        username?: string;
      };
    }>();
    historicosDrop.forEach(({ cardId, motivo_drop, observacoes, usuario }) => {
      if (motivo_drop || observacoes || usuario) {
        map.set(cardId, { motivo_drop, observacoes, usuario });
      }
    });
    return map;
  }, [historicosDrop]);

  // Lookup por freelancer_id para acessar score_reputacao e demais sinais
  // sem repetir o find em cada render.
  const freelancersById = useMemo(() => {
    const map = new Map<string, FreelancerListItem>();
    vagaFreelancers.forEach((f) => map.set(f.id, f));
    return map;
  }, [vagaFreelancers]);

  // Drag-and-drop só faz sentido na ordem manual do kanban — desabilitar quando
  // o usuário escolheu outra ordenação, senão ele move achando que mudou a ordem
  // persistida mas só estava vendo uma view diferente.
  const dragEnabled = sortMode === "kanban";

  // Agrupar cards por etapa, aplicando a ordenação escolhida
  const cardsByEtapa = useMemo(() => {
    const grouped: Record<string, KanbanCard[]> = {};
    const sortFn =
      sortMode === "score"
        ? (a: KanbanCard, b: KanbanCard) => {
            const sa = freelancersById.get(a.freelancer_id)?.score_reputacao ?? -1;
            const sb = freelancersById.get(b.freelancer_id)?.score_reputacao ?? -1;
            return sb - sa;
          }
        : (a: KanbanCard, b: KanbanCard) => a.ordem_na_etapa - b.ordem_na_etapa;
    etapas.forEach((etapa) => {
      grouped[etapa.id] = cardsData
        .filter((card) => card.etapa_atual_id === etapa.id)
        .sort(sortFn);
    });
    return grouped;
  }, [cardsData, etapas, sortMode, freelancersById]);

  const isLoading = isLoadingEtapas || isLoadingCards;

  // Garantir que a etapa correta seja selecionada após carregar as etapas
  useEffect(() => {
    if (etapas.length > 0) {
      // Se há um initialEtapaId, ele existe nas etapas, e ainda não foi aplicado, usar ele (apenas uma vez)
      if (initialEtapaId && etapas.some(e => e.id === initialEtapaId) && !hasAppliedInitialEtapa.current) {
        setSelectedEtapaId(initialEtapaId);
        hasAppliedInitialEtapa.current = true;
      }
      // Caso contrário, se não há seleção definida e não veio com initialEtapaId, aguardar 1 segundo antes de definir a primeira aba
      else if (!selectedEtapaId && !initialEtapaId && etapas[0]?.id) {
        const timer = setTimeout(() => {
          setSelectedEtapaId(etapas[0].id);
        }, 1000);

        return () => clearTimeout(timer);
      }
    }
  }, [etapas, selectedEtapaId, initialEtapaId]);

  // Invalida todas as queries que dependem da posição/etapa dos cards do kanban.
  // Centralizar evita que algum handler esqueça de atualizar uma das views (visual,
  // drop-in-place, métricas) e deixe a UI com estado inconsistente.
  const invalidateKanbanQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["kanban-cards", vagaId] });
    queryClient.invalidateQueries({ queryKey: ["kanban-cards-drop-in-place", vagaId] });
    queryClient.invalidateQueries({ queryKey: ["kanban-metricas", vagaId] });
  };

  // Handlers para drag & drop
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, cardId: string) => {
    e.dataTransfer.setData("cardId", cardId);
    e.currentTarget.classList.add("opacity-50");
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.classList.remove("opacity-50");
    setDraggedOverId(null);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, cardId: string) => {
    e.preventDefault();
    if (draggedOverId !== cardId) {
      setDraggedOverId(cardId);
    }
  };

  const handleDragLeave = () => {
    setDraggedOverId(null);
  };

  const handleDrop = async (
    e: React.DragEvent<HTMLDivElement>, 
    targetCardId: string,
    etapaId: string
  ) => {
    e.preventDefault();
    setDraggedOverId(null);
    
    const draggedId = e.dataTransfer.getData("cardId");
    if (draggedId === targetCardId) return;

    const cardsNaEtapa = cardsByEtapa[etapaId] || [];
    const draggedCard = cardsNaEtapa.find(c => c.id === draggedId);
    const targetCard = cardsNaEtapa.find(c => c.id === targetCardId);

    if (!draggedCard || !targetCard) return;

    const draggedIndex = cardsNaEtapa.indexOf(draggedCard);
    const targetIndex = cardsNaEtapa.indexOf(targetCard);

    if (draggedIndex === -1 || targetIndex === -1) return;

    try {
      await kanbanApi.reorderCard(draggedId, targetIndex, user?.id);
      invalidateKanbanQueries();
      toast({
        title: "Sucesso",
        description: "Candidato reordenado com sucesso!",
      });
    } catch (error) {
      console.error("Erro ao reordenar card:", error);
      toast({
        title: "Erro",
        description: "Não foi possível reordenar o candidato. " + reportUserError(error, { source: "kanban" }).description,
        variant: "destructive",
      });
    }
  };

  const handleChangeOrder = async (cardId: string, newOrder: number, etapaId: string) => {
    try {
      await kanbanApi.reorderCard(cardId, newOrder, user?.id);
      invalidateKanbanQueries();
      toast({
        title: "Sucesso",
        description: "Ordem alterada com sucesso!",
      });
    } catch (error) {
      console.error("Erro ao alterar ordem:", error);
      toast({
        title: "Erro",
        description: "Não foi possível alterar a ordem. " + reportUserError(error, { source: "kanban" }).description,
        variant: "destructive",
      });
    }
  };

  const handleMoveToEtapa = async (cardId: string, etapaDestinoId: string, etapaId: string) => {
    try {
      const cardsNaEtapa = cardsByEtapa[etapaId] || [];
      const card = cardsNaEtapa.find(c => c.id === cardId);
      if (!card) return;

      // Verificar se moveu para etapa de drop
      const destEtapa = etapas.find((e) => e.id === etapaDestinoId);
      if (destEtapa?.tipo === "drop") {
        // Abrir modal de reprovação ao invés de mostrar toast
        setDropCandidate(card);
        return;
      }

      // Mover para nova etapa (última posição)
      const cardsDestino = cardsByEtapa[etapaDestinoId] || [];
      const novaOrdem = cardsDestino.length;

      await kanbanApi.moveCard(
        cardId,
        etapaDestinoId,
        novaOrdem,
        undefined,
        user?.id
      );

      invalidateKanbanQueries();

      toast({
        title: "Sucesso",
        description: "Candidato movido com sucesso!",
      });
    } catch (error) {
      console.error("Erro ao mover card:", error);
      toast({
        title: "Erro",
        description: "Não foi possível mover o candidato. " + reportUserError(error, { source: "kanban" }).description,
        variant: "destructive",
      });
    }
  };

  const handleDropConfirmed = async (data: {
    motivos: string[];
    observacoes?: string;
    status?: FreelancerStatus;
  }) => {
    if (!dropCandidate) return;

    try {
      const reprovadoEtapa = etapas.find((e) => e.tipo === "drop");
      if (reprovadoEtapa) {
        // Primeiro, fazer o drop no kanban
        await kanbanApi.dropCard(
          dropCandidate.id,
          reprovadoEtapa.id,
          data.motivos,
          data.observacoes,
          user?.id
        );

        // Se um status foi fornecido (motivo relacionado a disponibilidade), atualizar o status do freelancer
        if (data.status && dropCandidate.freelancer_id) {
          const hoje = new Date().toISOString().split('T')[0]; // Data de hoje no formato YYYY-MM-DD
          await kanbanApi.updateFreelancerDisponibilidade(
            dropCandidate.freelancer_id,
            hoje,
            data.status,
            data.observacoes,
            user?.id
          );
          // Invalidar queries relacionadas ao freelancer para atualizar os dados
          queryClient.invalidateQueries({ queryKey: ["disponibilidade"] });
          queryClient.invalidateQueries({ queryKey: ["kanban-cards", vagaId] });
          // Invalidar listagem de freelancers para refletir a atualização
          invalidateFreelancers();
        }

        queryClient.invalidateQueries({ queryKey: ["kanban-cards", vagaId] });
        queryClient.invalidateQueries({ queryKey: ["kanban-metricas", vagaId] });
        toast({
          title: "Sucesso",
          description: "Candidato reprovado com sucesso!",
        });
      }
    } catch (error) {
      console.error("Erro ao reprovar candidato:", error);
      toast({
        title: "Erro",
        description: "Não foi possível reprovar o candidato. " + (error as Error).message,
        variant: "destructive",
      });
    } finally {
      setDropCandidate(null);
    }
  };

  const handleSelectFreelancers = () => {
    window.location.href = `/freelancers?vaga=${vagaId}`;
  };

  const handleDropInPlaceConfirmed = async (data: {
    motivos: string[];
    observacoes?: string;
  }) => {
    if (!dropInPlaceCandidate) return;

    try {
      await kanbanApi.dropCardInPlace(
        dropInPlaceCandidate.id,
        data.motivos,
        data.observacoes,
        user?.id
      );

      queryClient.invalidateQueries({ queryKey: ["kanban-cards", vagaId] });
      queryClient.invalidateQueries({ queryKey: ["kanban-cards-drop-in-place", vagaId] });
      queryClient.invalidateQueries({ queryKey: ["kanban-metricas", vagaId] });
      
      toast({
        title: "Sucesso",
        description: "Candidato descartado com sucesso!",
      });
    } catch (error) {
      console.error("Erro ao descartar candidato:", error);
      toast({
        title: "Erro",
        description: "Não foi possível descartar o candidato. " + (error as Error).message,
        variant: "destructive",
      });
    } finally {
      setDropInPlaceCandidate(null);
    }
  };

  const handleReactivateCard = async (
    card: KanbanCard,
    observacoes?: string
  ) => {
    try {
      await kanbanApi.reactivateCardInPlace(
        card.id,
        observacoes,
        user?.id
      );

      queryClient.invalidateQueries({ queryKey: ["kanban-cards", vagaId] });
      queryClient.invalidateQueries({ queryKey: ["kanban-cards-drop-in-place", vagaId] });
      queryClient.invalidateQueries({ queryKey: ["kanban-metricas", vagaId] });
      
      toast({
        title: "Sucesso",
        description: "Candidato reativado com sucesso!",
      });
    } catch (error) {
      console.error("Erro ao reativar candidato:", error);
      toast({
        title: "Erro",
        description: "Não foi possível reativar o candidato. " + reportUserError(error, { source: "kanban" }).description,
        variant: "destructive",
      });
      throw error;
    }
  };

  const refreshKanban = () => {
    queryClient.invalidateQueries({ queryKey: ["kanban-etapas", vagaId] });
    queryClient.invalidateQueries({ queryKey: ["kanban-cards", vagaId] });
    queryClient.invalidateQueries({ queryKey: ["kanban-cards-drop-in-place", vagaId] });
    queryClient.invalidateQueries({ queryKey: ["kanban-metricas", vagaId] });
  };

  // ====== Multi-select: handlers das ações em massa ======

  const isSelecting = selectedCardIds.size > 0;

  const handleComparar = () => {
    const ids = new Set(
      Array.from(selectedCardIds)
        .map((cardId) => cardsData.find((c) => c.id === cardId)?.freelancer_id)
        .filter((id): id is string => !!id),
    );
    const enriched = vagaFreelancers.filter((f) => ids.has(f.id));
    if (enriched.length < 2) {
      toast({
        variant: "destructive",
        title: "Nada para comparar",
        description: "Selecione ao menos 2 freelancers com dados disponíveis.",
      });
      return;
    }
    setFreelancersToCompare(enriched);
    setCompareDrawerOpen(true);
  };

  const handleEnviarParaOutraVaga = () => {
    const selected = cardsData.filter((c) => selectedCardIds.has(c.id));
    setCopyFreelancers(selected.map((c) => ({ id: c.freelancer_id, name: c.freelancer.nome })));
    setCopyModalOpen(true);
  };

  // Abre portfolio E LinkedIn de cada candidato selecionado em novas abas.
  // Browsers normalmente bloqueiam aberturas em lote — pedimos confirmação antes
  // para o usuário liberar pop-ups se necessário.
  const handleOpenLinks = () => {
    const selected = cardsData.filter((c) => selectedCardIds.has(c.id));
    const tabs: {
      url: string;
      freelancerId: string;
      origem: "linkedin" | "portfolio_externo";
    }[] = [];
    let semNenhum = 0;
    let invalidos = 0;
    selected.forEach((c) => {
      // Só entra na fila o que abre de verdade; link quebrado é contado à parte
      // para o toast não prometer aba que o navegador nunca vai abrir.
      const portfolio = externalUrlHref(c.freelancer.portfolio_url);
      const linkedin = parseLinkedInUrl(c.freelancer.linkedin_url).href;
      if (!c.freelancer.portfolio_url && !c.freelancer.linkedin_url) {
        semNenhum++;
        return;
      }
      if (c.freelancer.portfolio_url && !portfolio) invalidos++;
      if (c.freelancer.linkedin_url && !linkedin) invalidos++;
      if (portfolio)
        tabs.push({ url: portfolio, freelancerId: c.freelancer_id, origem: "portfolio_externo" });
      if (linkedin)
        tabs.push({ url: linkedin, freelancerId: c.freelancer_id, origem: "linkedin" });
    });

    const avisos = [
      semNenhum > 0 ? `${semNenhum} selecionado(s) sem link cadastrado.` : null,
      invalidos > 0 ? `${invalidos} link(s) inválido(s) foram ignorados.` : null,
    ].filter(Boolean);

    if (tabs.length === 0) {
      toast({
        variant: "destructive",
        title: "Nenhum link para abrir",
        description:
          invalidos > 0
            ? `Nenhum link válido nos selecionados. ${invalidos} link(s) inválido(s).`
            : "Nenhum dos selecionados tem portfolio ou LinkedIn cadastrado.",
      });
      return;
    }
    if (tabs.length > 1) {
      const ok = window.confirm(
        `Vamos abrir ${tabs.length} abas (portfolio + LinkedIn dos selecionados). Se o navegador bloquear, libere pop-ups para este site e tente de novo.`,
      );
      if (!ok) return;
    }
    tabs.forEach((t) => {
      trackFreelancerView(t.freelancerId, t.origem, { vaga_id: vagaId });
      openExternalUrl(t.url);
    });
    toast({
      title: `${tabs.length} aba${tabs.length > 1 ? "s" : ""} aberta${tabs.length > 1 ? "s" : ""}`,
      description: avisos.length > 0 ? avisos.join(" ") : undefined,
    });
  };

  // Copia para o clipboard um texto consolidado das negociações atuais da etapa
  // selecionada — formato pensado pra colar em Slack/e-mail.
  const handleCopiarStatusEtapa = async () => {
    const activeEtapaId = selectedEtapaId || etapas[0]?.id || "";
    const etapa = etapas.find((e) => e.id === activeEtapaId);
    if (!etapa) return;
    const cardsNaEtapa = cardsByEtapa[etapa.id] || [];
    const comNegociacao = countCardsComNegociacao(cardsNaEtapa);
    if (comNegociacao === 0) {
      toast({
        variant: "destructive",
        title: "Nada para copiar",
        description: "Nenhum candidato desta etapa tem negociação registrada.",
      });
      return;
    }
    const texto = formatStatusEtapaText(etapa.nome, cardsNaEtapa, (vaga as { moeda?: string | null } | undefined)?.moeda);
    try {
      await navigator.clipboard.writeText(texto);
      toast({
        title: "Status copiado",
        description: `${comNegociacao} candidato${comNegociacao > 1 ? "s" : ""} no texto.`,
      });
    } catch (error) {
      console.error("Falha ao copiar status da etapa:", error);
      toast({
        variant: "destructive",
        title: "Não foi possível copiar",
        description: "Tente novamente ou copie manualmente.",
      });
    }
  };

  // Copia para o clipboard os detalhes (nome, pretensão, áreas, localização,
  // portfolio, LinkedIn) de todos os candidatos da etapa selecionada — COR-357.
  const handleCopiarCandidatosEtapa = () => {
    const activeEtapaId = selectedEtapaId || etapas[0]?.id || "";
    const etapa = etapas.find((e) => e.id === activeEtapaId);
    if (!etapa) return;
    const cardsNaEtapa = cardsByEtapa[etapa.id] || [];
    if (cardsNaEtapa.length === 0) {
      toast({
        variant: "destructive",
        title: "Nada para copiar",
        description: "Nenhum candidato nesta etapa.",
      });
      return;
    }
    copyFreelancerDetailsList(
      cardsNaEtapa.map((card) => ({
        nome: card.freelancer.nome,
        pretensao_salarial: card.freelancer.pretensao_salarial,
        areas: card.freelancer.areas,
        city: card.freelancer.city,
        state: card.freelancer.state,
        portfolio_url: card.freelancer.portfolio_url,
        linkedin_url: card.freelancer.linkedin_url,
      })),
    );
  };

  // ========== FUNÇÕES AUXILIARES PARA PROJETOS ==========
  
  const getProjetoInfo = (freelancerId: string) => {
    const projetosDoFreelancer = projetosEmAndamento.get(freelancerId) || [];
    const projetoAtual = projetosDoFreelancer[0]; // Primeiro projeto (mais recente)
    const totalProjetos = projetosDoFreelancer.length;
    
    const diasRestantes = projetoAtual?.data_conclusao ? calcularDiasRestantes(projetoAtual.data_conclusao) : null;
    const proximoDeConcluir = diasRestantes !== null && diasRestantes >= 0 && diasRestantes < 10;

    return {
      projetosDoFreelancer,
      projetoAtual,
      totalProjetos,
      diasRestantes,
      proximoDeConcluir
    };
  };

  return (
    <div className="space-y-6">
      {/* Header com Métricas */}
      <Card className="bg-white/80 backdrop-blur-xs border border-slate-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-6">
            <div className="flex flex-col">
              <span className="text-2xl font-bold text-slate-900">
                {metricas?.total_candidatos || 0}
              </span>
              <span className="text-xs text-slate-500">Total Candidatos</span>
            </div>
            <div className="flex flex-col">
              <span className="text-2xl font-bold text-green-600">
                {metricas?.total_ativos || 0}
              </span>
              <span className="text-xs text-slate-500">Ativos</span>
            </div>
            <div 
              className="flex flex-col cursor-pointer hover:bg-slate-50 rounded-md p-2 -m-2 transition-colors"
              onClick={() => setShowDropInPlaceList(true)}
              title="Clique para ver candidatos descartados"
            >
              <span className="text-2xl font-bold text-orange-600">
                {metricas?.total_drops_in_place ?? cardsDropInPlace.length}
              </span>
              <span className="text-xs text-slate-500">Descartados</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Select value={sortMode} onValueChange={(v) => setSortMode(v as SortMode)}>
              <SelectTrigger className="h-9 w-[180px] text-xs">
                <SelectValue placeholder="Ordenar por" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="kanban">Posição no kanban</SelectItem>
                <SelectItem value="score">Score (maior primeiro)</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={refreshKanban}
              disabled={isLoading}
              title="Atualizar"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            {/* Ações da etapa ativa (não dependem de seleção) agrupadas num menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" title="Mais ações">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={handleCopiarStatusEtapa}
                  disabled={isLoading}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <ClipboardList className="h-4 w-4" />
                  Copiar status da etapa
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleCopiarCandidatosEtapa}
                  disabled={isLoading}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <ClipboardCopy className="h-4 w-4" />
                  Copiar candidatos da etapa
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="default"
              size="sm"
              onClick={handleSelectFreelancers}
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Adicionar Candidato
            </Button>
          </div>
        </div>
      </Card>

      {/* Abas por Etapa */}
      <Card className="bg-white/80 backdrop-blur-xs border border-slate-200">
        <Tabs value={selectedEtapaId || etapas[0]?.id || ""} onValueChange={setSelectedEtapaId} className="w-full">
          <TabsList className="grid w-[calc(100%-3rem)] p-1 rounded-lg mx-6 mt-6 bg-slate-100/50" style={{ gridTemplateColumns: `repeat(${etapas.length}, minmax(0, 1fr))` }}>
            {etapas.map((etapa) => {
              const cardsNaEtapa = cardsByEtapa[etapa.id] || [];
              return (
                <TabsTrigger
                  key={etapa.id}
                  value={etapa.id}
                  className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:shadow-xs"
                >
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: etapa.cor }}
                  />
                  <span>{etapa.nome}</span>
                  {cardsNaEtapa.length > 0 && (
                    <Badge variant="secondary" className="bg-slate-100 text-slate-700 border-slate-200 text-xs">
                      {cardsNaEtapa.length}
                    </Badge>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Selecionar todos: linha fixa e discreta acima dos cards, no mesmo
              padrão da listagem de freelancers; ganha destaque quando há seleção */}
          {(() => {
            const activeEtapaId = selectedEtapaId || etapas[0]?.id || "";
            const cardsNaEtapaAtiva = cardsByEtapa[activeEtapaId] || [];
            if (cardsNaEtapaAtiva.length === 0) return null;
            const allSelected = cardsNaEtapaAtiva.every((c) => selectedCardIds.has(c.id));
            return (
              <div className="ml-4 mr-6 mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedCardIds(new Set(cardsNaEtapaAtiva.map((c) => c.id)));
                    } else {
                      setSelectedCardIds(new Set());
                    }
                  }}
                  aria-label="Selecionar todos desta etapa"
                />
                <span>
                  Selecionar os {cardsNaEtapaAtiva.length} desta etapa
                  {isSelecting &&
                    ` · ${selectedCardIds.size} selecionado${selectedCardIds.size !== 1 ? "s" : ""}`}
                </span>
              </div>
            );
          })()}

          {etapas.map((etapa) => {
            const cardsNaEtapa = cardsByEtapa[etapa.id] || [];

            return (
              <TabsContent key={etapa.id} value={etapa.id} className="px-6 pb-6 pt-4">
                <div className="space-y-4">
                  {cardsNaEtapa.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                      <div className="w-20 h-20 mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-dashed border-slate-300 rounded" />
                      </div>
                      <p className="text-lg font-medium">Nenhum candidato nesta etapa</p>
                      <p className="text-sm mt-1">Use o botão "Adicionar Candidato" para adicionar</p>
                    </div>
                  ) : (
                    cardsNaEtapa.map((card, index) => (
                      <div
                        key={card.id}
                        draggable={dragEnabled}
                        onDragStart={dragEnabled ? (e) => handleDragStart(e, card.id) : undefined}
                        onDragEnd={dragEnabled ? handleDragEnd : undefined}
                        onDragOver={dragEnabled ? (e) => handleDragOver(e, card.id) : undefined}
                        onDragLeave={dragEnabled ? handleDragLeave : undefined}
                        onDrop={dragEnabled ? (e) => handleDrop(e, card.id, etapa.id) : undefined}
                        className={`
                          group relative p-5 rounded-xl transition-all duration-200 border
                          ${draggedOverId === card.id 
                            ? 'border-blue-300 bg-blue-50/50 scale-[1.01] shadow-lg' 
                            : 'bg-white/80 border-slate-200 hover:bg-white hover:shadow-md'} 
                          ${card.is_dropped ? 'opacity-70' : ''}
                        `}
                      >
                        {/* Checkbox de seleção sempre visível; a ordem na etapa fica
                            junto do nome, não mais neste canto */}
                        <div className="absolute -top-3 -left-3 z-10">
                          <Checkbox
                            checked={selectedCardIds.has(card.id)}
                            onCheckedChange={(checked) => {
                              setSelectedCardIds((prev) => {
                                const next = new Set(prev);
                                if (checked) next.add(card.id);
                                else next.delete(card.id);
                                return next;
                              });
                            }}
                            aria-label={`Selecionar ${card.freelancer.nome}`}
                            className="h-6 w-6 bg-white border-2 border-slate-300 shadow-sm data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 hover:border-blue-500"
                          />
                        </div>

                        {/* Handle de drag e links */}
                        <div className="absolute top-4 right-4 flex items-center gap-2">
                          {/* Links de Portfolio e LinkedIn */}
                          <ExternalLinkButton
                            kind="portfolio"
                            url={card.freelancer.portfolio_url}
                            hideWhenEmpty={false}
                            onOpen={() =>
                              trackFreelancerView(card.freelancer_id, "portfolio_externo", { vaga_id: vagaId })
                            }
                            iconClassName="h-3 w-3"
                          />
                          <FreelancerLinkedInButton
                            freelancerId={card.freelancer_id}
                            url={card.freelancer.linkedin_url}
                            hideWhenEmpty={false}
                            onOpen={() =>
                              trackFreelancerView(card.freelancer_id, "linkedin", { vaga_id: vagaId })
                            }
                            iconClassName="h-4 w-4"
                          />
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFalarComFrila(card);
                            }}
                            variant="ghost"
                            size="icon"
                            disabled={checkingFrilaId === card.id}
                            className={`h-7 w-7 transition-colors ${
                              card.freelancer.falar_com_frila
                                ? "text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                                : "text-slate-600 hover:bg-primary/10 hover:text-primary"
                            }`}
                            title="Fazer Amizade"
                          >
                            <HeartHandshake
                              className={`h-4 w-4 ${checkingFrilaId === card.id ? "animate-pulse" : ""}`}
                            />
                          </Button>

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                              {etapa.nome?.trim().toLowerCase() === "shortlist" && (
                                <>
                                  <DropdownMenuItem onSelect={() => setNegociacaoCandidate(card)}>
                                    <Handshake className="mr-2 h-4 w-4" />
                                    {card.negociacao_atual ? "Atualizar negociação" : "Atribuir negociação"}
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                </>
                              )}
                              <DropdownMenuItem
                                onSelect={() =>
                                  copyFreelancerDetails({
                                    nome: card.freelancer.nome,
                                    pretensao_salarial: card.freelancer.pretensao_salarial,
                                    areas: card.freelancer.areas,
                                    city: card.freelancer.city,
                                    state: card.freelancer.state,
                                    portfolio_url: card.freelancer.portfolio_url,
                                    linkedin_url: card.freelancer.linkedin_url,
                                  })
                                }
                              >
                                <ClipboardCopy className="mr-2 h-4 w-4" />
                                Copiar detalhes do freelancer
                              </DropdownMenuItem>
                              {vaga?.agencia_id && (
                                <DropdownMenuItem
                                  onSelect={() =>
                                    setIndicarCandidate({
                                      freelancerId: card.freelancer_id,
                                      nome: card.freelancer.nome,
                                    })
                                  }
                                >
                                  <Radar className="mr-2 h-4 w-4" />
                                  Match com esta agência...
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger>
                                  <ArrowUpDown className="mr-2 h-4 w-4" />
                                  Mudar ordem
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent>
                                  {cardsNaEtapa.map((_, idx) => (
                                    <DropdownMenuItem
                                      key={idx}
                                      onClick={() => handleChangeOrder(card.id, idx, etapa.id)}
                                      disabled={idx === index}
                                    >
                                      {idx === index && "✓ "}
                                      Posição {idx + 1}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                              <DropdownMenuSeparator />
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger>
                                  <ChevronRight className="mr-2 h-4 w-4" />
                                  Enviar para
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent>
                                  {etapas
                                    .filter(e => e.id !== etapa.id)
                                    .map((etapaDestino) => (
                                      <DropdownMenuItem
                                        key={etapaDestino.id}
                                        onClick={() => handleMoveToEtapa(card.id, etapaDestino.id, etapa.id)}
                                      >
                                        <div
                                          className="mr-2 h-3 w-3 rounded-full"
                                          style={{ backgroundColor: etapaDestino.cor }}
                                        />
                                        {etapaDestino.nome}
                                      </DropdownMenuItem>
                                    ))}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                              <DropdownMenuSeparator />
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger>
                                  <Copy className="mr-2 h-4 w-4" />
                                  Enviar para outra vaga
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setCopyFreelancers([{ id: card.freelancer_id, name: card.freelancer.nome }]);
                                      setCopyModalOpen(true);
                                    }}
                                  >
                                    Enviar esse...
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      // Inicia a seleção já com este card marcado
                                      setSelectedCardIds(new Set([card.id]));
                                    }}
                                  >
                                    Selecionar múltiplos...
                                  </DropdownMenuItem>
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setDropInPlaceCandidate(card)}
                                className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              >
                                <EyeOff className="mr-2 h-4 w-4" />
                                Descartar candidato
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <div className="cursor-grab text-slate-400 hover:text-slate-600 transition-colors">
                            <GripVertical className="h-5 w-5" />
                          </div>
                        </div>
                        
                        <div className="flex gap-4">
                          {/* Avatar e informações principais */}
                          <div className="shrink-0">
                            <Link
                              to={`/freelancers/${card.freelancer.id}?fromVaga=${vagaId}&vagaTab=freelancers&etapaId=${etapa.id}`}
                              className="block"
                              onClick={(event) => openCandidatoOnPlainClick(event, card.freelancer.id)}
                            >
                              <Avatar
                                data-avatar
                                className="cursor-pointer w-16 h-16 shadow-md border-2 border-white"
                              >
                                <AvatarFallback className="bg-linear-to-r from-blue-100 to-purple-100 text-blue-700 text-lg font-bold">
                                  {getInitials(card.freelancer.nome)}
                                </AvatarFallback>
                              </Avatar>
                            </Link>
                          </div>
                          
                          {/* Informações do freelancer */}
                          <div className="flex-1 min-w-0 space-y-3">
                            {/* Header com nome + score + negociação.
                                pr-44 reserva espaço pro cluster de ícones (globo/
                                LinkedIn/amizade/menu/grip) que fica absoluto no topo
                                direito, evitando que o score fique espremido/cortado. */}
                            <div className="min-w-0 pr-44">
                              <h3 className="font-bold text-slate-900 text-lg leading-tight">
                                <span
                                  className="mr-1.5 align-middle text-xs font-semibold text-slate-400"
                                  title={`Posição ${index + 1} nesta etapa`}
                                >
                                  {index + 1}º
                                </span>
                                <Link
                                  to={`/freelancers/${card.freelancer.id}?fromVaga=${vagaId}&vagaTab=freelancers&etapaId=${etapa.id}`}
                                  className="cursor-pointer hover:text-blue-600 transition-colors"
                                  data-testid="abrir-candidato"
                                  onClick={(event) => openCandidatoOnPlainClick(event, card.freelancer.id)}
                                >
                                  {card.freelancer.nome}
                                </Link>
                              </h3>
                              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                                <DisponibilidadeLinha
                                  summary={dispKanbanMap?.get(card.freelancer_id) ?? null}
                                  level="chip"
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 gap-1 px-1.5 text-[11px] font-normal text-slate-500 hover:text-blue-600"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDisponibilidadeCard(card);
                                  }}
                                  title="Atualizar disponibilidade"
                                >
                                  <Pencil className="h-3 w-3" />
                                  Atualizar
                                </Button>
                                {card.freelancer.is_active === false && (
                                  <Badge
                                    variant="outline"
                                    className="border-red-200 bg-red-50 px-1.5 py-0 text-[10px] text-red-600"
                                  >
                                    inativo
                                  </Badge>
                                )}
                                {(() => {
                                  const f = freelancersById.get(card.freelancer_id);
                                  return f ? (
                                    <FreelancerScoreBadge freelancer={f} size="sm" />
                                  ) : null;
                                })()}
                                {matchDaAgencia.has(card.freelancer_id) && (
                                  <Badge
                                    className="text-xs bg-emerald-600 text-white hover:bg-emerald-600/90"
                                    title="Match manual com a agência desta vaga"
                                  >
                                    <Radar className="mr-1 h-3 w-3" />
                                    Match c/ agência
                                  </Badge>
                                )}
                                {card.negociacao_atual && (
                                  <Badge
                                    variant="outline"
                                    className="text-xs border-blue-200 bg-blue-50 text-blue-700"
                                  >
                                    {negociacaoStatusIcon(card.negociacao_atual.status)}{" "}
                                    {negociacaoStatusLabel(card.negociacao_atual.status)}
                                  </Badge>
                                )}
                                {card.freelancer.falar_com_frila && (
                                  <Badge
                                    variant="outline"
                                    className="text-xs border-orange-200 bg-orange-50 text-orange-700"
                                  >
                                    <HeartHandshake className="h-3 w-3 mr-1" />
                                    Falar com o Frila
                                  </Badge>
                                )}
                              </div>
                            </div>

                            {/* Informações em grid */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 text-sm">
                              {/* Pretensão salarial */}
                              {card.freelancer.pretensao_salarial && (
                                <div className="flex items-center gap-2 text-slate-600">
                                  <DollarSign className="h-4 w-4 shrink-0 text-green-500" />
                                  <span className="truncate">{formatCurrency(card.freelancer.pretensao_salarial)}</span>
                                </div>
                              )}

                              {/* Área de atuação */}
                              {card.freelancer.areas && (
                                <div className="flex items-center gap-2 text-slate-600">
                                  <Briefcase className="h-4 w-4 shrink-0 text-purple-500" />
                                  <span className="truncate">{card.freelancer.areas}</span>
                                </div>
                              )}

                              {/* Localização */}
                              {(card.freelancer.city || card.freelancer.state || card.freelancer.country) && (
                                <div className="flex items-center gap-2 text-slate-600">
                                  <MapPin className="h-4 w-4 shrink-0 text-red-500" />
                                  <span className="truncate">
                                    {[
                                      card.freelancer.city,
                                      card.freelancer.state,
                                      card.freelancer.country
                                    ].filter(Boolean).join(', ')}
                                  </span>
                                </div>
                              )}

                              {/* Tempo na etapa */}
                              <div className="flex items-center gap-2 text-slate-600">
                                <Clock className="h-4 w-4 shrink-0 text-blue-500" />
                                <span className="truncate">
                                  {formatDistanceToNow(new Date(card.data_adicao), {
                                    addSuffix: true,
                                    locale: ptBR
                                  })}
                                </span>
                              </div>

                              {/* Movimentações */}
                              {card.total_movimentacoes > 0 && (
                                <div
                                  className="flex items-center gap-2 text-slate-600 cursor-pointer hover:text-blue-600 transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    trackFreelancerView(card.freelancer_id, "kanban_historico", { vaga_id: vagaId });
                                    setHistoryCandidate(card);
                                  }}
                                >
                                  <ArrowRight className="h-4 w-4 shrink-0 text-slate-400" />
                                  <span className="truncate">
                                    {card.total_movimentacoes} {card.total_movimentacoes === 1 ? 'movimentação' : 'movimentações'}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Projeto em andamento. A disponibilidade agora vive no badge
                                unificado + botão "Atualizar" do header (fonte única). */}
                            {(() => {
                              const { projetoAtual, totalProjetos, diasRestantes, proximoDeConcluir } = getProjetoInfo(card.freelancer_id);

                              return (
                                <div className="pt-2 border-t border-slate-100">
                                  <div
                                    className={`space-y-2 p-2 -mx-2 rounded-md transition-colors ${totalProjetos > 0 ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                                    onClick={(e) => {
                                      if (totalProjetos > 0) {
                                        e.stopPropagation();
                                        trackFreelancerView(card.freelancer_id, "projetos_modal", { vaga_id: vagaId });
                                        setSelectedFreelancerProjetos({
                                          freelancerId: card.freelancer_id,
                                          nome: card.freelancer.nome
                                        });
                                      }
                                    }}
                                    title={totalProjetos > 0 ? "Clique para ver todos os projetos" : ""}
                                  >
                                    {projetoAtual ? (
                                      <>
                                        <div className="flex items-center gap-2 text-xs">
                                          <FolderOpen className="h-3.5 w-3.5 shrink-0 text-green-500" />
                                          <span className="text-green-700 font-medium">
                                            {totalProjetos > 1 ? `${totalProjetos} Projetos` : '1 Projeto'}
                                          </span>
                                          {diasRestantes !== null && (
                                            <Badge
                                              variant="outline"
                                              className={`text-[10px] px-1 py-0 h-4 ${
                                                proximoDeConcluir
                                                  ? 'bg-orange-50 text-orange-700 border-orange-300'
                                                  : 'bg-slate-50 text-slate-600 border-slate-300'
                                              }`}
                                            >
                                              {diasRestantes}d
                                            </Badge>
                                          )}
                                        </div>
                                        {projetoAtual.agencia?.nome && (
                                          <div className="flex items-center gap-2 text-xs">
                                            <Building2 className="h-3.5 w-3.5 shrink-0 text-blue-500" />
                                            <span className="text-blue-700 font-medium truncate">
                                              {projetoAtual.agencia.nome}
                                            </span>
                                          </div>
                                        )}
                                        <div className="flex items-start gap-2 text-xs text-slate-600">
                                          <Briefcase className="h-3.5 w-3.5 shrink-0 text-slate-400 mt-0.5" />
                                          <span className="line-clamp-2">{projectArea(projetoAtual)}</span>
                                        </div>
                                      </>
                                    ) : (
                                      <div className="flex items-center gap-2 text-xs text-slate-400">
                                        <FolderClosed className="h-3.5 w-3.5 shrink-0" />
                                        <span>Sem Projeto</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Observações */}
                            <CardObservacoes
                              count={observacoesCounts[card.id] ?? 0}
                              onClick={() => {
                                setObservacoesCard(card);
                              }}
                            />

                            {/* Informações de Reprovação - Mostrar apenas se estiver na etapa de drop */}
                            {etapa.tipo === "drop" && (() => {
                              const dropInfo = dropInfoMap.get(card.id);
                              if (!dropInfo || (!dropInfo.motivo_drop && !dropInfo.observacoes && !dropInfo.usuario)) {
                                return null;
                              }
                              return (
                                <div className="pt-3 border-t border-red-100 space-y-2">
                                  {dropInfo.usuario && (
                                    <div className="flex items-center gap-2 text-xs text-slate-600 mb-1">
                                      <span className="text-slate-500">Reprovado por:</span>
                                      <span className="font-medium">
                                        {dropInfo.usuario.full_name || dropInfo.usuario.username || "Usuário desconhecido"}
                                      </span>
                                    </div>
                                  )}
                                  {hasMotivos(dropInfo.motivo_drop) && (
                                    <div className="flex items-start gap-2">
                                      <AlertCircle className="h-3.5 w-3.5 shrink-0 text-red-600 mt-0.5" />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-red-700 mb-1">
                                          {parseDropReasons(dropInfo.motivo_drop).length > 1 ? 'Motivos da Reprovação' : 'Motivo da Reprovação'}
                                        </p>
                                        <div className="bg-red-50 border border-red-100 rounded-md p-2">
                                          <div className="text-xs text-slate-700">
                                            {formatMotivosLista(dropInfo.motivo_drop)}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  {dropInfo.observacoes && (
                                    <div className="flex items-start gap-2">
                                      <FileText className="h-3.5 w-3.5 shrink-0 text-slate-400 mt-0.5" />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-slate-700 mb-1">
                                          Descrição
                                        </p>
                                        <p className="text-xs text-slate-600 whitespace-pre-wrap wrap-break-word">
                                          {dropInfo.observacoes}
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </Card>

      {/* Modal de Histórico */}
      <CandidateHistoryModal
        open={!!historyCandidate}
        onOpenChange={(open) => !open && setHistoryCandidate(null)}
        candidate={historyCandidate}
        historico={historico}
      />

      {/* Editor de disponibilidade: mesmo dialog por eixos (+ WhatsApp + datas) do
          detalhe do freelancer. O dialog invalida ["disponibilidade"] no sucesso,
          então o badge do card atualiza sozinho. */}
      {disponibilidadeCard && (
        <AtualizarDisponibilidadeDialog
          open={!!disponibilidadeCard}
          onOpenChange={(open) => !open && setDisponibilidadeCard(null)}
          freelancerId={disponibilidadeCard.freelancer.id}
          freelancerNome={disponibilidadeCard.freelancer.nome}
        />
      )}

      {/* Modal de Observações */}
      {observacoesCard && (
        <ObservacoesModal
          open={!!observacoesCard}
          onOpenChange={(open) => !open && setObservacoesCard(null)}
          posicaoId={observacoesCard.id}
          freelancerNome={observacoesCard.freelancer.nome}
        />
      )}

      {/* Modal de Reprovação */}
      <DropCandidateModal
        open={!!dropCandidate}
        onOpenChange={(open) => !open && setDropCandidate(null)}
        candidate={dropCandidate}
        onConfirm={handleDropConfirmed}
      />

      {/* Modal de Ocultar (Drop In-Place) */}
      <DropInPlaceModal
        open={!!dropInPlaceCandidate}
        onOpenChange={(open) => !open && setDropInPlaceCandidate(null)}
        candidate={dropInPlaceCandidate}
        onConfirm={handleDropInPlaceConfirmed}
      />

      {/* Modal de Lista de Candidatos Ocultos */}
      <DropInPlaceListModal
        open={showDropInPlaceList}
        onOpenChange={setShowDropInPlaceList}
        cards={cardsDropInPlace}
        etapas={etapas}
        onReactivate={handleReactivateCard}
        isLoading={isLoadingDropInPlace}
        vagaId={vagaId}
      />

      {/* Modal de Projetos do Freelancer */}
      {selectedFreelancerProjetos && (
        <FreelancerProjetosModal
          open={!!selectedFreelancerProjetos}
          onOpenChange={(open) => !open && setSelectedFreelancerProjetos(null)}
          freelancerNome={selectedFreelancerProjetos.nome}
          projetos={projetosEmAndamento.get(selectedFreelancerProjetos.freelancerId) || []}
        />
      )}

      {/* Enviar candidato(s) deste quadro para outra vaga */}
      <EnviarParaVagaDialog
        open={copyModalOpen}
        onOpenChange={(open) => {
          setCopyModalOpen(open);
          if (!open) setSelectedCardIds(new Set());
        }}
        freelancers={copyFreelancers.map((f) => ({ id: f.id, nome: f.name }))}
        currentVagaId={vagaId}
        onSuccess={() => setSelectedCardIds(new Set())}
      />

      <CopyFallbackDialog
        open={copyFallbackOpen}
        onOpenChange={setCopyFallbackOpen}
        text={copyFallbackText}
      />

      <FreelancerMultiSelectBar
        count={selectedCardIds.size}
        onClear={() => setSelectedCardIds(new Set())}
        onCompare={handleComparar}
        onSendToVaga={handleEnviarParaOutraVaga}
        onOpenLinks={handleOpenLinks}
        vagaId={vagaId}
      />

      <FreelancerCompareDrawer
        open={compareDrawerOpen}
        onOpenChange={setCompareDrawerOpen}
        freelancers={freelancersToCompare}
        onRemove={(id) => {
          setFreelancersToCompare((prev) => prev.filter((f) => f.id !== id));
        }}
        vaga={vaga ?? null}
      />

      <NegociacaoModal
        open={!!negociacaoCandidate}
        onOpenChange={(open) => !open && setNegociacaoCandidate(null)}
        candidate={negociacaoCandidate}
        moeda={(vaga as { moeda?: string | null } | undefined)?.moeda}
      />

      {falarComFrilaCard && (
        <FalarComFrilaModal
          isOpen={!!falarComFrilaCard}
          onClose={() => {
            setFalarComFrilaCard(null);
            setFalarComFrilaPending(null);
            queryClient.invalidateQueries({ queryKey: ["kanban-cards", vagaId] });
          }}
          freelancerId={falarComFrilaCard.freelancer.id}
          freelancerNome={falarComFrilaCard.freelancer.nome}
          existingActivity={falarComFrilaPending}
        />
      )}

      <IndicarAgenciaDialog
        open={!!indicarCandidate}
        onOpenChange={(open) => !open && setIndicarCandidate(null)}
        freelancerId={indicarCandidate?.freelancerId}
        freelancerName={indicarCandidate?.nome}
        agenciaIdInicial={vaga?.agencia_id ?? undefined}
      />

      <CandidatoPanel
        freelancerId={candidatoPanel.itemId}
        tab={candidatoPanel.tab}
        onTabChange={candidatoPanel.setTab}
        onClose={candidatoPanel.close}
        vagaId={vagaId}
      />
    </div>
  );
}

// Componente para exibir prévia das observações no card. A contagem vem do
// batch no nível da vaga (observacoesCounts) — antes cada card disparava sua
// própria query (N+1). O componente agora é apenas apresentacional.
function CardObservacoes({ count, onClick }: { count: number; onClick: () => void }) {
  return (
    <div
      className="pt-3 border-t border-slate-100 flex items-center gap-2 text-xs cursor-pointer hover:text-blue-600 transition-colors group/obs"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title="Clique para ver e adicionar observações"
    >
      <MessageSquare className="h-3.5 w-3.5 shrink-0 text-slate-400 group-hover/obs:text-blue-600 transition-colors" />
      <span className={count > 0 ? "text-slate-700 group-hover/obs:text-blue-600" : "text-slate-400 group-hover/obs:text-blue-600"}>
        {count > 0 ? `${count} observação${count > 1 ? 'ões' : ''}` : "Adicionar observação"}
      </span>
    </div>
  );
}

